import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Conditional import for Google AI
let GoogleGenerativeAI: any = null;
try {
  // const googleAI = require('@google/generative-ai');
  // GoogleGenerativeAI = googleAI.GoogleGenerativeAI;
} catch (error) {
  console.warn('Google AI package not installed, AI features will be disabled');
}

export interface UserMatch {
  id: string;
  userId: string;
  matchedUserId: string;
  matchScore: number;
  matchReasons: string[];
  createdAt: Timestamp;
  lastInteraction?: Timestamp;
  status: 'pending' | 'accepted' | 'rejected' | 'connected';
}

export interface PodcastSuggestion {
  id: string;
  title: string;
  description: string;
  participants: string[];
  maxParticipants: number;
  currentParticipants: number;
  topics: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedDuration: number; // in minutes
  suggestedDate: Date;
  suggestedTime?: string;
  studioAvailable?: boolean;
  status: 'suggested' | 'scheduled' | 'completed' | 'cancelled';
  createdAt: Timestamp;
  lastSuggested?: Timestamp;
}

export interface MatchCriteria {
  interests: string[];
  learningGoals: string[];
  offerings: string[];
  experienceLevel: 'beginner' | 'intermediate' | 'advanced';
  industry: string;
  location?: string;
  availability: 'weekdays' | 'weekends' | 'flexible';
}

class AIMatchmakingService {
  private readonly MATCH_COLLECTION = 'userMatches';
  private readonly PODCAST_SUGGESTIONS_COLLECTION = 'podcastSuggestions';
  private readonly USER_PROFILES_COLLECTION = 'userProfiles';
  private genAI: any = null;
  private readonly USE_AI_MATCHING = process.env.REACT_APP_GOOGLE_AI_API_KEY && process.env.REACT_APP_GOOGLE_AI_API_KEY !== 'your_google_ai_api_key_here';

  constructor() {
    if (this.USE_AI_MATCHING && GoogleGenerativeAI) {
      this.genAI = new GoogleGenerativeAI(process.env.REACT_APP_GOOGLE_AI_API_KEY!);
    }
  }

  /**
   * AI-powered user matching based on multiple criteria
   */
  async findMatches(userId: string, limit: number = 10): Promise<UserMatch[]> {
    try {
      // Get current user profile
      const userProfile = await this.getUserProfile(userId);
      if (!userProfile) {
        throw new Error('User profile not found');
      }

      // Get all other user profiles
      const allProfiles = await this.getAllUserProfiles(userId);
      
      // Calculate match scores for each potential match
      const potentialMatches = allProfiles.map(profile => ({
        profile,
        matchScore: this.calculateMatchScore(userProfile, profile),
        matchReasons: this.generateMatchReasons(userProfile, profile)
      }));

      // Sort by match score and take top matches
      const topMatches = potentialMatches
        .sort((a, b) => b.matchScore - a.matchScore)
        .slice(0, limit);

      // Check for existing matches to avoid duplicates
      const existingMatches = await this.getExistingMatches(userId);
      const existingMatchIds = existingMatches.map(match => 
        match.matchedUserId === userId ? match.userId : match.matchedUserId
      );

      // Filter out existing matches and create new ones
      const newMatches = topMatches
        .filter(match => !existingMatchIds.includes(match.profile.uid))
        .map(match => ({
          userId,
          matchedUserId: match.profile.uid,
          matchScore: match.matchScore,
          matchReasons: match.matchReasons,
          createdAt: Timestamp.now(),
          status: 'pending' as const
        }));

      // Save new matches to Firestore
      const savedMatches: UserMatch[] = [];
      for (const match of newMatches) {
        const docRef = await addDoc(collection(db, this.MATCH_COLLECTION), match);
        savedMatches.push({
          id: docRef.id,
          ...match
        });
      }

      return savedMatches;
    } catch (error) {
      console.error('Error finding matches:', error);
      throw error;
    }
  }

  /**
   * Enhance matches with AI-powered analysis
   */
  private async enhanceMatchesWithAI(userProfile: any, matches: any[]): Promise<any[]> {
    if (!this.USE_AI_MATCHING || !this.genAI || matches.length === 0) {
      return matches;
    }

    try {
      const model = this.genAI.getGenerativeModel({ model: "gemini-2.5-flash" });
      
      // Process matches in batches to avoid rate limits
      const batchSize = 3;
      const enhancedMatches = [];
      
      for (let i = 0; i < matches.length; i += batchSize) {
        const batch = matches.slice(i, i + batchSize);
        const batchPromises = batch.map(async (match) => {
          try {
            const aiAnalysis = await this.getAIMatchAnalysis(userProfile, match.profile, model);
            return {
              ...match,
              matchScore: Math.round((match.matchScore + aiAnalysis.score) / 2), // Average rule-based and AI scores
              matchReasons: [...match.matchReasons, ...aiAnalysis.reasons].slice(0, 3) // Max 3 reasons
            };
          } catch (error) {
            console.warn('AI analysis failed for match, using rule-based result:', error);
            return match;
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        enhancedMatches.push(...batchResults);
      }
      
      return enhancedMatches;
    } catch (error) {
      console.warn('AI enhancement failed, using rule-based matches:', error);
      return matches;
    }
  }

  /**
   * Get AI-powered match analysis
   */
  private async getAIMatchAnalysis(user1: any, user2: any, model: any): Promise<{score: number, reasons: string[]}> {
    const prompt = this.buildAIMatchingPrompt(user1, user2);
    
    const result = await model.generateContent(prompt);
    const response = await result.response;
    const text = response.text();
    
    try {
      const analysis = JSON.parse(text);
      return {
        score: Math.max(0, Math.min(100, analysis.matchScore || 50)),
        reasons: analysis.matchReasons || []
      };
    } catch (error) {
      console.warn('Failed to parse AI response:', error);
      return { score: 50, reasons: [] };
    }
  }

  /**
   * Build AI matching prompt
   */
  private buildAIMatchingPrompt(user1: any, user2: any): string {
    return `
Je bent een expert in business networking en ondernemerschap. Analyseer de compatibiliteit tussen deze twee ondernemers voor potentiële samenwerking, kennisuitwisseling of business opportunities.

Gebruiker 1:
- Naam: ${user1.firstName || ''} ${user1.lastName || ''} (${user1.displayName})
- Bio: ${user1.bio || 'Geen bio beschikbaar'}
- Bedrijf: ${user1.company || 'Geen bedrijf'}
- Vestiging: ${user1.companyLocation || 'Onbekend'}
- Werkgebied: ${user1.workScope || 'Onbekend'}${user1.workScopeCountry ? ` (${user1.workScopeCountry})` : ''}
- Interesses: ${(user1.interests || []).join(', ')}
- Leerdoelen: ${(user1.learningGoals || []).join(', ')}
- Aanbod: ${(user1.offerings || []).join(', ')}
- Ervaring: ${user1.experienceLevel || 'Onbekend'}
- Industrie: ${user1.industry || 'Onbekend'}
- Locatie: ${user1.location || 'Onbekend'}

Gebruiker 2:
- Naam: ${user2.firstName || ''} ${user2.lastName || ''} (${user2.displayName})
- Bio: ${user2.bio || 'Geen bio beschikbaar'}
- Bedrijf: ${user2.company || 'Geen bedrijf'}
- Vestiging: ${user2.companyLocation || 'Onbekend'}
- Werkgebied: ${user2.workScope || 'Onbekend'}${user2.workScopeCountry ? ` (${user2.workScopeCountry})` : ''}
- Interesses: ${(user2.interests || []).join(', ')}
- Leerdoelen: ${(user2.learningGoals || []).join(', ')}
- Aanbod: ${(user2.offerings || []).join(', ')}
- Ervaring: ${user2.experienceLevel || 'Onbekend'}
- Industrie: ${user2.industry || 'Onbekend'}
- Locatie: ${user2.location || 'Onbekend'}

Geef je antwoord in dit JSON formaat:
{
  "matchScore": 85,
  "matchReasons": [
    "Beide ondernemers in dezelfde industrie met complementaire expertise",
    "Gedeelde interesse in digitale transformatie en innovatie",
    "Potentiële samenwerking tussen lokaal en nationaal werkgebied"
  ]
}

Focus op:
1. Business compatibiliteit en samenwerkingsmogelijkheden
2. Kennisuitwisseling en leerpotentieel
3. Geografische en werkgebied compatibiliteit
4. Industrie en expertise overlap
5. Complementaire vaardigheden en aanbod

Geef een score van 0-100 en maximaal 3 concrete redenen voor de match.
`;
  }

  /**
   * Test AI integration (for development)
   */
  async testAIIntegration(): Promise<{success: boolean, message: string}> {
    if (!this.USE_AI_MATCHING || !this.genAI) {
      return {
        success: false,
        message: 'AI integration not available - check API key configuration'
      };
    }

    try {
      const model = this.genAI.getGenerativeModel({ model: "gemini-2.5-flash" });
      
      const testPrompt = `
Je bent een expert in business networking. Geef een korte analyse van deze test match:

Gebruiker 1: Software ondernemer uit Amsterdam, geïnteresseerd in AI en innovatie
Gebruiker 2: Marketing consultant uit Rotterdam, geïnteresseerd in digitale transformatie

Geef je antwoord in JSON formaat:
{
  "matchScore": 75,
  "matchReasons": ["Gedeelde interesse in digitale transformatie", "Complementaire expertise"]
}
`;

      const result = await model.generateContent(testPrompt);
      const response = await result.response;
      const text = response.text();
      
      const analysis = JSON.parse(text);
      
      return {
        success: true,
        message: `AI integration working! Test score: ${analysis.matchScore}, Reasons: ${analysis.matchReasons.join(', ')}`
      };
    } catch (error) {
      return {
        success: false,
        message: `AI integration failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Calculate match score between two users (0-100)
   */
  private calculateMatchScore(user1: any, user2: any): number {
    let score = 0;
    let totalWeight = 0;

    // Interests matching (weight: 30%)
    const interestWeight = 30;
    const interestScore = this.calculateInterestOverlap(user1.interests || [], user2.interests || []);
    score += interestScore * interestWeight;
    totalWeight += interestWeight;

    // Learning goals matching (weight: 25%)
    const learningWeight = 25;
    const learningScore = this.calculateLearningGoalCompatibility(user1.learningGoals || [], user2.offerings || []);
    score += learningScore * learningWeight;
    totalWeight += learningWeight;

    // Offerings matching (weight: 25%)
    const offeringWeight = 25;
    const offeringScore = this.calculateLearningGoalCompatibility(user2.learningGoals || [], user1.offerings || []);
    score += offeringScore * offeringWeight;
    totalWeight += offeringWeight;

    // Experience level compatibility (weight: 10%)
    const experienceWeight = 10;
    const experienceScore = this.calculateExperienceCompatibility(user1.experienceLevel, user2.experienceLevel);
    score += experienceScore * experienceWeight;
    totalWeight += experienceWeight;

  // Industry compatibility (weight: 10%)
  const industryWeight = 10;
  const industryScore = this.calculateIndustryCompatibility(user1.industry, user2.industry);
  score += industryScore * industryWeight;
  totalWeight += industryWeight;

  // Company compatibility (weight: 5%)
  const companyWeight = 5;
  const companyScore = this.calculateCompanyCompatibility(user1.company, user2.company);
  score += companyScore * companyWeight;
  totalWeight += companyWeight;

  // Location compatibility (weight: 5%)
  const locationWeight = 5;
  const locationScore = this.calculateLocationCompatibility(user1.companyLocation, user2.companyLocation);
  score += locationScore * locationWeight;
  totalWeight += locationWeight;

  // Work scope compatibility (weight: 5%)
  const workScopeWeight = 5;
  const workScopeScore = this.calculateWorkScopeCompatibility(user1.workScope, user2.workScope, user1.workScopeCountry, user2.workScopeCountry);
  score += workScopeScore * workScopeWeight;
  totalWeight += workScopeWeight;

    return Math.round(score / totalWeight);
  }

  /**
   * Calculate interest overlap score
   */
  private calculateInterestOverlap(interests1: string[], interests2: string[]): number {
    if (interests1.length === 0 || interests2.length === 0) return 0;
    
    const normalized1 = interests1.map(i => i.toLowerCase().trim());
    const normalized2 = interests2.map(i => i.toLowerCase().trim());
    
    const intersection = normalized1.filter(interest => normalized2.includes(interest));
    const union = Array.from(new Set([...normalized1, ...normalized2]));
    
    return (intersection.length / union.length) * 100;
  }

  /**
   * Calculate learning goal and offering compatibility
   */
  private calculateLearningGoalCompatibility(learningGoals: string[], offerings: string[]): number {
    if (learningGoals.length === 0 || offerings.length === 0) return 0;
    
    const normalizedGoals = learningGoals.map(g => g.toLowerCase().trim());
    const normalizedOfferings = offerings.map(o => o.toLowerCase().trim());
    
        const matches = normalizedGoals.filter((goal: string) =>
      normalizedOfferings.some((offering: string) =>
        offering.includes(goal) || goal.includes(offering)
      )
    );
    
    return (matches.length / normalizedGoals.length) * 100;
  }

  /**
   * Calculate experience level compatibility
   */
  private calculateExperienceCompatibility(level1: string, level2: string): number {
    if (level1 === level2) return 100;
    if ((level1 === 'beginner' && level2 === 'intermediate') || 
        (level1 === 'intermediate' && level2 === 'beginner') ||
        (level1 === 'intermediate' && level2 === 'advanced') ||
        (level1 === 'advanced' && level2 === 'intermediate')) {
      return 80;
    }
    return 40; // beginner-advanced or other combinations
  }

  /**
   * Calculate industry compatibility
   */
  private calculateIndustryCompatibility(industry1: string, industry2: string): number {
    if (!industry1 || !industry2) return 50;
    if (industry1.toLowerCase() === industry2.toLowerCase()) return 100;
    
    // Related industries (you can expand this)
    const relatedIndustries: { [key: string]: string[] } = {
      'technology': ['software', 'it', 'digital', 'ai', 'machine learning'],
      'finance': ['banking', 'investment', 'accounting', 'insurance'],
      'healthcare': ['medical', 'pharmaceutical', 'wellness', 'fitness'],
      'education': ['training', 'consulting', 'coaching', 'mentoring'],
      'marketing': ['advertising', 'pr', 'social media', 'content creation']
    };
    
    const industry1Lower = industry1.toLowerCase();
    const industry2Lower = industry2.toLowerCase();
    
    for (const [main, related] of Object.entries(relatedIndustries)) {
      if ((main === industry1Lower || related.includes(industry1Lower)) &&
          (main === industry2Lower || related.includes(industry2Lower))) {
        return 75;
      }
    }
    
    return 25;
  }

  /**
   * Calculate company compatibility
   */
  private calculateCompanyCompatibility(company1: string, company2: string): number {
    if (!company1 || !company2) return 50;
    if (company1.toLowerCase() === company2.toLowerCase()) return 100;
    
    // Check for similar company names (partial match)
    const company1Words = company1.toLowerCase().split(' ');
    const company2Words = company2.toLowerCase().split(' ');
    
    const commonWords = company1Words.filter(word => 
      company2Words.some(word2 => word2.includes(word) || word.includes(word2))
    );
    
    if (commonWords.length > 0) {
      return 75;
    }
    
    return 25;
  }

  /**
   * Calculate location compatibility
   */
  private calculateLocationCompatibility(location1: string, location2: string): number {
    if (!location1 || !location2) return 50;
    if (location1.toLowerCase() === location2.toLowerCase()) return 100;
    
    // Check for same city or country
    const location1Parts = location1.toLowerCase().split(',').map(part => part.trim());
    const location2Parts = location2.toLowerCase().split(',').map(part => part.trim());
    
    // Same city
    if (location1Parts[0] === location2Parts[0]) return 90;
    
    // Same country
    if (location1Parts.length > 1 && location2Parts.length > 1) {
      if (location1Parts[1] === location2Parts[1]) return 70;
    }
    
    // Same region (Netherlands, Belgium, etc.)
    const regions = {
      'netherlands': ['nederland', 'holland', 'nl'],
      'belgium': ['belgië', 'belgique', 'be'],
      'germany': ['deutschland', 'de'],
      'france': ['france', 'fr']
    };
    
    for (const [region, variants] of Object.entries(regions)) {
      const location1InRegion = location1Parts.some(part => 
        variants.some(variant => part.includes(variant))
      );
      const location2InRegion = location2Parts.some(part => 
        variants.some(variant => part.includes(variant))
      );
      
      if (location1InRegion && location2InRegion) return 60;
    }
    
    return 30;
  }

  /**
   * Calculate work scope compatibility
   */
  private calculateWorkScopeCompatibility(scope1: string, scope2: string, country1?: string, country2?: string): number {
    if (!scope1 || !scope2) return 50;
    if (scope1 === scope2) {
      // Same scope - check if same country for national
      if (scope1 === 'nationaal' && country1 && country2) {
        if (country1 === country2) return 100;
        // Same region (Benelux, DACH, etc.)
        if (this.isSameRegion(country1, country2)) return 85;
        return 70; // Different countries but both national
      }
      return 100;
    }
    
    // Compatible combinations
    const compatibleScopes = {
      'lokaal': ['nationaal'],      // Lokaal kan samenwerken met nationaal
      'nationaal': ['lokaal', 'internationaal'], // Nationaal kan met beide
      'internationaal': ['nationaal'] // Internationaal kan met nationaal
    };
    
    if (compatibleScopes[scope1 as keyof typeof compatibleScopes]?.includes(scope2)) {
      // Check country compatibility for national scope
      if (scope1 === 'nationaal' || scope2 === 'nationaal') {
        const nationalCountry = scope1 === 'nationaal' ? country1 : country2;
        const otherCountry = scope1 === 'nationaal' ? country2 : country1;
        
        if (nationalCountry && otherCountry) {
          if (nationalCountry === otherCountry) return 85;
          if (this.isSameRegion(nationalCountry, otherCountry)) return 80;
        }
      }
      return 75;
    }
    
    // Lokaal + Internationaal is minder compatibel
    if ((scope1 === 'lokaal' && scope2 === 'internationaal') || 
        (scope1 === 'internationaal' && scope2 === 'lokaal')) {
      return 40;
    }
    
    return 25;
  }

  /**
   * Check if two countries are in the same region
   */
  private isSameRegion(country1: string, country2: string): boolean {
    const regions = {
      'Benelux': ['Nederland', 'België', 'Luxemburg'],
      'DACH': ['Duitsland', 'Oostenrijk', 'Zwitserland'],
      'Nordic': ['Denemarken', 'Zweden', 'Noorwegen', 'Finland'],
      'EU': ['Nederland', 'België', 'Duitsland', 'Frankrijk', 'Spanje', 'Italië', 'Oostenrijk', 'Zwitserland', 'Denemarken', 'Zweden', 'Noorwegen', 'Finland', 'Polen', 'Tsjechië', 'Hongarije', 'Portugal', 'Griekenland', 'Ierland', 'Luxemburg']
    };
    
    for (const [region, countries] of Object.entries(regions)) {
      if (countries.includes(country1) && countries.includes(country2)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Generate human-readable match reasons
   */
  private generateMatchReasons(user1: any, user2: any): string[] {
    const reasons: string[] = [];
    
    // Interest overlap
    const interests1 = user1.interests || [];
    const interests2 = user2.interests || [];
        const commonInterests = interests1.filter((i: string) =>
      interests2.some((j: string) => j.toLowerCase().includes(i.toLowerCase()) || i.toLowerCase().includes(j.toLowerCase()))
    );
    
    if (commonInterests.length > 0) {
      reasons.push(`Gedeelde interesses: ${commonInterests.slice(0, 3).join(', ')}`);
    }
    
    // Learning-offering match
    const learningGoals1 = user1.learningGoals || [];
    const offerings2 = user2.offerings || [];
        const learningMatches = learningGoals1.filter((goal: string) =>
      offerings2.some((offering: string) =>
        offering.toLowerCase().includes(goal.toLowerCase()) || goal.toLowerCase().includes(offering.toLowerCase())
      )
    );
    
    if (learningMatches.length > 0) {
      reasons.push(`${user2.displayName || 'Deze persoon'} kan je helpen met: ${learningMatches.slice(0, 2).join(', ')}`);
    }
    
    // Experience level
    if (user1.experienceLevel && user2.experienceLevel) {
      if (user1.experienceLevel === user2.experienceLevel) {
        reasons.push('Vergelijkbaar ervaringsniveau');
      } else if (Math.abs(this.getExperienceLevelNumber(user1.experienceLevel) - this.getExperienceLevelNumber(user2.experienceLevel)) === 1) {
        reasons.push('Complementair ervaringsniveau');
      }
    }
    
    // Industry
    if (user1.industry && user2.industry && user1.industry.toLowerCase() === user2.industry.toLowerCase()) {
      reasons.push('Zelfde industrie');
    }
    
    // Company compatibility
    if (user1.company && user2.company) {
      if (user1.company.toLowerCase() === user2.company.toLowerCase()) {
        reasons.push(`Beide werkzaam bij ${user1.company}`);
      } else {
        const company1Words = user1.company.toLowerCase().split(' ');
        const company2Words = user2.company.toLowerCase().split(' ');
        const commonWords = company1Words.filter((word: string) => 
          company2Words.some((word2: string) => word2.includes(word) || word.includes(word2))
        );
        if (commonWords.length > 0) {
          reasons.push(`Vergelijkbare bedrijven: ${user1.company} & ${user2.company}`);
        }
      }
    }
    
    // Location compatibility
    if (user1.companyLocation && user2.companyLocation) {
      const location1Parts = user1.companyLocation.toLowerCase().split(',').map((part: string) => part.trim());
      const location2Parts = user2.companyLocation.toLowerCase().split(',').map((part: string) => part.trim());
      
      if (location1Parts[0] === location2Parts[0]) {
        reasons.push(`Beide gevestigd in ${location1Parts[0]}`);
      } else if (location1Parts.length > 1 && location2Parts.length > 1 && location1Parts[1] === location2Parts[1]) {
        reasons.push(`Beide gevestigd in ${location1Parts[1]}`);
      }
    }
    
    // Work scope compatibility
    if (user1.workScope && user2.workScope) {
      if (user1.workScope === user2.workScope) {
        if (user1.workScope === 'nationaal' && user1.workScopeCountry && user2.workScopeCountry) {
          if (user1.workScopeCountry === user2.workScopeCountry) {
            reasons.push(`Beide nationaal werkgebied in ${user1.workScopeCountry}`);
          } else if (this.isSameRegion(user1.workScopeCountry, user2.workScopeCountry)) {
            reasons.push(`Beide nationaal werkgebied in dezelfde regio (${user1.workScopeCountry} & ${user2.workScopeCountry})`);
          } else {
            reasons.push(`Beide nationaal werkgebied (${user1.workScopeCountry} & ${user2.workScopeCountry})`);
          }
        } else {
          const scopeLabels = {
            'lokaal': 'lokaal werkgebied',
            'nationaal': 'nationaal werkgebied', 
            'internationaal': 'internationaal werkgebied'
          };
          reasons.push(`Beide ${scopeLabels[user1.workScope as keyof typeof scopeLabels]}`);
        }
      } else {
        const compatibleScopes = {
          'lokaal': ['nationaal'],
          'nationaal': ['lokaal', 'internationaal'],
          'internationaal': ['nationaal']
        };
        
        if (compatibleScopes[user1.workScope as keyof typeof compatibleScopes]?.includes(user2.workScope)) {
          const scopeLabels = {
            'lokaal': 'lokaal',
            'nationaal': 'nationaal',
            'internationaal': 'internationaal'
          };
          let reason = `Complementaire werkgebieden: ${scopeLabels[user1.workScope as keyof typeof scopeLabels]} & ${scopeLabels[user2.workScope as keyof typeof scopeLabels]}`;
          
          // Add country info if relevant
          if (user1.workScope === 'nationaal' && user1.workScopeCountry) {
            reason += ` (${user1.workScopeCountry})`;
          }
          if (user2.workScope === 'nationaal' && user2.workScopeCountry) {
            reason += ` (${user2.workScopeCountry})`;
          }
          
          reasons.push(reason);
        }
      }
    }
    
    return reasons.slice(0, 3); // Max 3 reasons
  }

  private getExperienceLevelNumber(level: string): number {
    switch (level.toLowerCase()) {
      case 'beginner': return 1;
      case 'intermediate': return 2;
      case 'advanced': return 3;
      default: return 2;
    }
  }

  /**
   * Generate AI-powered podcast suggestions
   */
  async generatePodcastSuggestions(): Promise<PodcastSuggestion[]> {
    try {
      // Get all user profiles
      const allProfiles = await this.getAllUserProfiles();
      
      // Check studio availability first
      const studioAvailability = await this.getStudioAvailability();
      
      // Generate suggestions based on current themes and evergreen topics
      const suggestions: PodcastSuggestion[] = [];
      
      // Current themes (you can update these regularly)
      const currentThemes = [
        'AI en Machine Learning in Business',
        'Duurzaam Ondernemen',
        'Remote Work Best Practices',
        'Digital Transformation',
        'Cybersecurity Trends'
      ];
      
      // Evergreen topics
      const evergreenTopics = [
        'Leadership Development',
        'Innovation Strategies',
        'Customer Experience',
        'Team Building',
        'Strategic Planning'
      ];
      
      // Combine themes
      const allTopics = [...currentThemes, ...evergreenTopics];
      
      for (const topic of allTopics) {
        const participants = this.findParticipantsForTopic(allProfiles, topic);
        
        if (participants.length >= 2) {
          // Find available studio time slot
          const availableSlot = this.findAvailableStudioSlot(studioAvailability, participants);
          
          if (availableSlot) {
            const suggestion: PodcastSuggestion = {
              id: `suggestion_${Date.now()}_${Math.random()}`,
              title: `Podcast: ${topic}`,
              description: `Een diepgaande discussie over ${topic} met experts uit de SuperKonnected community.`,
              participants: participants.slice(0, 5).map(p => p.uid),
              maxParticipants: 5,
              currentParticipants: 0,
              topics: [topic],
              difficulty: this.determineDifficulty(participants),
              estimatedDuration: 45,
              suggestedDate: availableSlot.date,
              suggestedTime: availableSlot.time,
              studioAvailable: true,
              status: 'suggested',
              createdAt: Timestamp.now()
            };
            
            suggestions.push(suggestion);
          }
        }
      }
      
      // Check for recent suggestions to avoid repetition
      const recentSuggestions = await this.getRecentPodcastSuggestions();
      const filteredSuggestions = suggestions.filter(suggestion => 
        !recentSuggestions.some(recent => 
          recent.title === suggestion.title && 
          recent.createdAt.toDate() > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) // 3 months
        )
      );
      
      // Save new suggestions
      const savedSuggestions: PodcastSuggestion[] = [];
      for (const suggestion of filteredSuggestions.slice(0, 10)) { // Max 10 suggestions
        const docRef = await addDoc(collection(db, this.PODCAST_SUGGESTIONS_COLLECTION), suggestion);
        savedSuggestions.push({
          ...suggestion,
          id: docRef.id
        });
      }
      
      return savedSuggestions;
    } catch (error) {
      console.error('Error generating podcast suggestions:', error);
      throw error;
    }
  }

  /**
   * Find participants for a specific topic
   */
  private findParticipantsForTopic(profiles: any[], topic: string): any[] {
    const topicKeywords = topic.toLowerCase().split(' ');
    
    return profiles
      .filter(profile => {
        const interests = (profile.interests || []).join(' ').toLowerCase();
        const offerings = (profile.offerings || []).join(' ').toLowerCase();
        const bio = (profile.bio || '').toLowerCase();
        
        return topicKeywords.some(keyword => 
          interests.includes(keyword) || 
          offerings.includes(keyword) || 
          bio.includes(keyword)
        );
      })
      .sort((a, b) => {
        // Sort by relevance score
        const scoreA = this.calculateTopicRelevance(a, topicKeywords);
        const scoreB = this.calculateTopicRelevance(b, topicKeywords);
        return scoreB - scoreA;
      });
  }

  /**
   * Get studio availability from calendar
   */
  private async getStudioAvailability(): Promise<any[]> {
    try {
      // Get studio calendar events
      const studioQuery = query(
        collection(db, 'studioCalendar'),
        where('type', '==', 'studio_booking'),
        orderBy('startTime', 'asc')
      );
      const snapshot = await getDocs(studioQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        startTime: doc.data().startTime?.toDate(),
        endTime: doc.data().endTime?.toDate()
      }));
    } catch (error) {
      console.error('Error getting studio availability:', error);
      return [];
    }
  }

  /**
   * Find available studio time slot for participants
   */
  private findAvailableStudioSlot(studioAvailability: any[], participants: any[]): { date: Date; time: string } | null {
    try {
      // Get participant availability
      const participantIds = participants.map(p => p.uid);
      
      // Check next 14 days for availability
      for (let day = 1; day <= 14; day++) {
        const checkDate = new Date();
        checkDate.setDate(checkDate.getDate() + day);
        
        // Skip weekends
        if (checkDate.getDay() === 0 || checkDate.getDay() === 6) continue;
        
        // Check studio availability for this day
        const dayStart = new Date(checkDate);
        dayStart.setHours(9, 0, 0, 0);
        const dayEnd = new Date(checkDate);
        dayEnd.setHours(17, 0, 0, 0);
        
        // Check if studio is free during business hours
        const studioBookings = studioAvailability.filter(booking => {
          const bookingStart = new Date(booking.startTime);
          const bookingEnd = new Date(booking.endTime);
          return bookingStart.toDateString() === checkDate.toDateString();
        });
        
        // Find free time slots (2-hour blocks for podcast recording)
        const timeSlots = this.generateTimeSlots(dayStart, dayEnd, 120); // 2 hours
        
        for (const slot of timeSlots) {
          const isSlotFree = !studioBookings.some(booking => {
            const bookingStart = new Date(booking.startTime);
            const bookingEnd = new Date(booking.endTime);
            return (
              (slot.start >= bookingStart && slot.start < bookingEnd) ||
              (slot.end > bookingStart && slot.end <= bookingEnd) ||
              (slot.start <= bookingStart && slot.end >= bookingEnd)
            );
          });
          
          if (isSlotFree) {
            return {
              date: checkDate,
              time: slot.start.toTimeString().slice(0, 5)
            };
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error finding studio slot:', error);
      return null;
    }
  }

  /**
   * Generate time slots for a given period
   */
  private generateTimeSlots(startTime: Date, endTime: Date, durationMinutes: number): { start: Date; end: Date }[] {
    const slots = [];
    const current = new Date(startTime);
    
    while (current < endTime) {
      const slotEnd = new Date(current.getTime() + durationMinutes * 60000);
      if (slotEnd <= endTime) {
        slots.push({
          start: new Date(current),
          end: new Date(slotEnd)
        });
      }
      current.setTime(current.getTime() + durationMinutes * 60000);
    }
    
    return slots;
  }

  /**
   * Calculate how relevant a user is to a topic
   */
  private calculateTopicRelevance(profile: any, topicKeywords: string[]): number {
    let score = 0;
    const interests = (profile.interests || []).join(' ').toLowerCase();
    const offerings = (profile.offerings || []).join(' ').toLowerCase();
    const bio = (profile.bio || '').toLowerCase();
    
    for (const keyword of topicKeywords) {
      if (interests.includes(keyword)) score += 3;
      if (offerings.includes(keyword)) score += 3;
      if (bio.includes(keyword)) score += 1;
    }
    
    return score;
  }

  /**
   * Determine podcast difficulty based on participants
   */
  private determineDifficulty(participants: any[]): 'beginner' | 'intermediate' | 'advanced' {
    const levels = participants.map(p => p.experienceLevel).filter(Boolean);
    
    if (levels.length === 0) return 'intermediate';
    
    const levelNumbers = levels.map(l => this.getExperienceLevelNumber(l));
    const averageLevel = levelNumbers.reduce((a, b) => a + b, 0) / levelNumbers.length;
    
    if (averageLevel >= 2.5) return 'advanced';
    if (averageLevel >= 1.5) return 'intermediate';
    return 'beginner';
  }

  /**
   * Accept or reject a match
   */
  async respondToMatch(matchId: string, response: 'accepted' | 'rejected'): Promise<void> {
    try {
      const matchRef = doc(db, this.MATCH_COLLECTION, matchId);
      await updateDoc(matchRef, {
        status: response,
        lastInteraction: Timestamp.now()
      });
    } catch (error) {
      console.error('Error responding to match:', error);
      throw error;
    }
  }

  /**
   * Get user's matches
   */
  async getUserMatches(userId: string): Promise<UserMatch[]> {
    try {
      const q = query(
        collection(db, this.MATCH_COLLECTION),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserMatch[];
    } catch (error) {
      console.error('Error getting user matches:', error);
      throw error;
    }
  }

  /**
   * Get podcast suggestions
   */
  async getPodcastSuggestions(): Promise<PodcastSuggestion[]> {
    try {
      const q = query(
        collection(db, this.PODCAST_SUGGESTIONS_COLLECTION),
        where('status', '==', 'suggested')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PodcastSuggestion[];
    } catch (error) {
      console.error('Error getting podcast suggestions:', error);
      throw error;
    }
  }

  // Helper methods
  private async getUserProfile(userId: string): Promise<any> {
    const q = query(
      collection(db, this.USER_PROFILES_COLLECTION),
      where('uid', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs[0]?.data();
  }

  private async getAllUserProfiles(excludeUserId?: string): Promise<any[]> {
    const querySnapshot = await getDocs(collection(db, this.USER_PROFILES_COLLECTION));
    return querySnapshot.docs
      .map(doc => ({ ...doc.data(), uid: doc.data().uid || doc.id }))
      .filter(profile => !excludeUserId || profile.uid !== excludeUserId);
  }

  private async getExistingMatches(userId: string): Promise<UserMatch[]> {
    const q = query(
      collection(db, this.MATCH_COLLECTION),
      where('userId', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as UserMatch[];
  }

  private async getRecentPodcastSuggestions(): Promise<PodcastSuggestion[]> {
    const threeMonthsAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, this.PODCAST_SUGGESTIONS_COLLECTION),
      where('createdAt', '>=', Timestamp.fromDate(threeMonthsAgo))
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as PodcastSuggestion[];
  }
}

export const aiMatchmakingService = new AIMatchmakingService();
